import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Container, Button, Typography, Box, CircularProgress, Card, CardContent, IconButton } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import workAnalyzerService from '../services/workAnalyzerService';
import InspectionRegistryListDisplay from '../components/InspectionEvaluationComponents/InspectionRegistryListDisplay';
import { InspectionRegistryStub } from '../models/inspectionEvaluationIssue';

const InspectionEvaluationHome: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [currentPendingId, setCurrentPendingId] = useState<string | null>(null);
  const [registry, setRegistry] = useState<InspectionRegistryStub[]>([]); 

  const fetchRegistry = async () => {
    try {
      const data = await workAnalyzerService.getInspectionRegistry();
      setRegistry(data.inspectionRegistry ?? []);
    } catch (error) {
      console.error("Failed to fetch registry:", error);
    }
  };

  useEffect(() => {
    fetchRegistry();
  }, []);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleClearFile = () => {
    setFile(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!file) return;

    setLoading(true);
    setLoadingMessage("Uploading...");
    const tempId = `temp-${Date.now()}`;

    // Add a "PENDING" item to the registry and clear the file
    setRegistry((prevRegistry) => [
      { id: tempId, date: new Date(), address: "Pending Analysis...", inspectionLink: "", status: "PENDING" },
      ...prevRegistry,
    ]);
    setFile(null);  // Clear the file after adding to registry

    try {
      const uploadResult = await workAnalyzerService.createInspectionRecord(file);
      if (uploadResult?.id) {
        setCurrentPendingId(uploadResult.id);
        setLoadingMessage("Analyzing inspection data...");

        await workAnalyzerService.createNewInspectionEvaluation(uploadResult.id);
        setLoadingMessage("File successfully uploaded and inspection data generated.");
        setCurrentPendingId(null);

        fetchRegistry();
      } else {
        setLoadingMessage("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("File upload error:", error);
      setLoadingMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 4 }}>
      <Box sx={{ mt: 4, width: '100%' }}>
        <Typography component="h1" variant="h5" gutterBottom>
          Inspections
        </Typography>
        {loadingMessage && (
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            {loadingMessage}
          </Typography>
        )}
        {registry ? 
        <InspectionRegistryListDisplay
          registry={registry}
          currentPendingId={currentPendingId}
          setCurrentPendingId={setCurrentPendingId}
          setLoadingMessage={setLoadingMessage}
          fetchRegistry={fetchRegistry}
        /> : <h3> No Inspections Found! Enter your first inspection below. </h3>
        }
        <Card variant="outlined" sx={{ mt: 3, width: '100%' }}>
          <CardContent>
            <Typography component="h2" variant="h6" gutterBottom>
              New Inspection
            </Typography>
            <Box component="form" onSubmit={handleSubmit} encType="multipart/form-data" sx={{ mt: 2 }}>
              {!file ? (
                <Button variant="contained" component="label" fullWidth>
                  Select File
                  <input type="file" hidden onChange={handleFileChange} />
                </Button>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PictureAsPdfIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="body1">{file.name}</Typography>
                  </Box>
                  <IconButton onClick={handleClearFile}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}

              {file && (
                <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                  Analyze
                </Button>
              )}

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body1" sx={{ ml: 2 }}>
                    {loadingMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default InspectionEvaluationHome;
